import { SelectHTMLAttributes, useId, useState } from 'react';

export type SelectOption = string | { value: string; label: string };

/** Select — native dropdown styled to match Input, with chevron affordance. */
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
}

/**
 * Select — native dropdown styled to match Input, with a chevron affordance.
 * options: array of strings or { value, label }.
 */
export function Select({ label, error, id: idProp, options = [], placeholder, value, disabled, style, ...props }: SelectProps) {
  // See Input: without a generated fallback, a label with no `id` never
  // associates with its control.
  const autoId = useId();
  const id = idProp ?? autoId;
  const [focus, setFocus] = useState(false);
  const errored = !!error;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, opacity: disabled ? 0.5 : 1 }}>
      {label ? (
        <label htmlFor={id} style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', fontWeight: 500, color: 'var(--text-soft)' }}>{label}</label>
      ) : null}
      <div style={{ position: 'relative' }}>
        <select
          id={id}
          value={value}
          disabled={disabled}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{
            width: '100%',
            appearance: 'none',
            WebkitAppearance: 'none',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body)',
            color: value ? 'var(--text)' : 'var(--text-dim)',
            background: 'var(--bg)',
            border: `1px solid ${errored ? 'rgb(var(--crimson))' : focus ? 'rgb(var(--gold))' : 'var(--line)'}`,
            borderRadius: 'var(--r-field)',
            padding: '10px 36px 10px 12px',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            boxShadow: focus ? `0 0 0 3px rgb(var(--gold) / .2)` : 'none',
            transition: 'border-color .2s, box-shadow .2s',
            ...style,
          }}
          {...props}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map((opt) => {
            const v = typeof opt === 'string' ? opt : opt.value;
            const l = typeof opt === 'string' ? opt : opt.label;
            return <option key={v} value={v}>{l}</option>;
          })}
        </select>
        <i className="ti ti-chevron-down" style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', pointerEvents: 'none', fontSize: 'calc(16px * var(--text-scale))' }} />
      </div>
      {errored ? <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption)', color: 'rgb(var(--crimson))' }}>{error}</p> : null}
    </div>
  );
}

export default Select;
